import React, { useMemo, useState, useCallback } from 'react';

import { OwnerBlock } from 'components/pages/certificates/productPage/OwnerBlock';
import { getNameOrEmailSlug, isLightColor } from 'utils/parsers';
import { format } from 'date-fns';
import { Box, Stack, useTheme } from '@mui/material';
import {
  Map,
  MapItem,
  getScanLocationLabel,
  getScanStatus,
} from '@repo/ui';
import { filterScans, type ScanFilterCriteria } from 'utils/scanFilters';
import { ScanStatusChip } from 'components/pages/certificates/ScanStatusChip';
import { SealOpenedDrawer } from 'pages/CertificatePage/components/SealOpenedDrawer';
import { useScanLogs } from 'hooks/api/useScanLogs';
import { useGetCertificatePublic } from 'hooks/api/useGetCertificatePublic';
import { useSelector } from 'store/hooks';
import { formatActivityTime } from 'utils/date';
import {
  CertificateDisplayStatusBorderColor,
  CertificateDisplayStatusTextColor,
} from 'utils/displayStatusColorMapper';
import { font } from 'settings/tokens';

const GOOGLE_MAPS_API_KEY = import.meta.env.APP_GOOGLE_MAPS_API_KEY;

/** Upper bound on scans plotted on the certificate map in one fetch (VERA-476).
 *  Clustering keeps the render tractable well past this; raise it if real
 *  certificates start exceeding it. */
const MAP_SCAN_LIMIT = 500;

/**
 * Height of one scan row in the stack list, in px — the number the "show N.5 rows" cap is
 * derived from. Measured in the browser rather than assumed: it is the OwnerBlock's avatar
 * + two text lines plus the row's own vertical padding. If the row's contents change,
 * re-measure (`getBoundingClientRect().height` on a row) rather than nudging this by eye,
 * because a value that drifts above the real height stops cutting the second row and the
 * list silently loses its "there is more" affordance.
 */
const STACK_ROW_HEIGHT = 55;

interface MapHistoryProps {
  mapHeight: string;
  defaultZoom: number;
  certificateId?: string;
  /**
   * Same criteria the list applies. The map used to receive nothing but a certificate
   * id, so search / status / date / location silently affected the list ONLY and
   * switching to map view looked like every filter had reset (VERA-476).
   */
  filters?: ScanFilterCriteria;
  /**
   * VERA-315 — open with ONE scan already selected: panned to, pulsing, and shown in
   * the docked card. Set when the screen was reached from something that names a
   * specific scan (today: the certificate page's suspicious-activity state card,
   * which points at the invalid scan it is reporting).
   *
   * ⚠️ BEST EFFORT, and the caller must not promise otherwise. A scan with no
   * coordinates is filtered out of the plotted set below, so there is no pin to
   * select and the map simply opens on the full spread. `Map` handles the
   * not-found case by leaving the selection uncommitted rather than erroring — see
   * its `idx < 0` guard.
   */
  focusScanId?: string | null;
}

export const MapHistory: React.FC<MapHistoryProps> = ({
  defaultZoom,
  mapHeight,
  certificateId: propCertificateId,
  filters,
  focusScanId,
}) => {
  const reduxCertificateId = useSelector(state => state.certificates.certificate?.id);
  const certificateId = propCertificateId || reduxCertificateId;

  /**
   * The certificate, from the SAME react-query cache `CertificatePage` reads. Keyed by id,
   * so this is a cache hit, not a second request.
   *
   * Deliberately NOT the redux `certificates.certificate`. That object is missing
   * `tag_opened_at` entirely and carries null support contacts — verified in the store at
   * runtime — so the "Opened" sheet rendered with neither its date nor its contact block.
   * The two are dispatched from the same response but do not stay in step, and the sheet
   * needs the same payload the page's own copy of it gets.
   */
  const { data: certificate } = useGetCertificatePublic(certificateId as string);

  /**
   * VERA-628's "Opened" sheet, opened by tapping an Opened chip on a scan row.
   *
   * Fed the CERTIFICATE's `tag_opened_at`, never the tapped scan's date. A seal is opened
   * once; every later scan merely re-reports that it is open. Passing the row's own date
   * would have the sheet assert "the seal was opened on <that day>" for each of four rows
   * in a stack — four different days, at most one of them true.
   */
  const [sealSheetOpen, setSealSheetOpen] = useState(false);
  const { palette } = useTheme();

  /**
   * The open stacked pin's scans, or null when a single scan is selected. Owning this here
   * (rather than letting the map draw a tooltip) is what keeps ONE panel describing what
   * you clicked — see the note on `renderOverlay`.
   */
  const [stackItems, setStackItems] = useState<MapItem[] | null>(null);

  // Selecting a single pin, or paging with the card's prev/next arrows, must retire an
  // open stack — otherwise the card keeps listing the stack while the map's selection
  // (and its pulse) has moved somewhere else entirely, which is the same stale-panel bug
  // in the other direction.
  const handleMarkerClick = useCallback(() => setStackItems(null), []);

  const isLightTheme = useMemo(() => {
    return isLightColor(palette.background.default);
  }, [palette.background.default]);

  // VERA-476: the map took the hook's default limit of 20, so it silently plotted only
  // the 20 most recent scans — a map showing a subset of pins is actively misleading
  // (it reads as "these are all the places this item has been"). The map has no
  // "load more" affordance, so it fetches a single large page instead. Rows without
  // coordinates are filtered out below, so the ceiling applies to fetched scans, not
  // rendered pins.
  const { data: history, isLoading: isLoadingScans } = useScanLogs({
    certificateId,
    limit: MAP_SCAN_LIMIT,
  });

  const getScanList = () => {
    if (history === null) {
      return [];
    }

    // Same predicate as the list, so the two views never disagree about what's shown.
    const rows = filterScans(history?.data ?? [], filters);

    const list = rows.map((item, index) => ({
      /*
       * VERA-315: the SCAN's own id, not `scan-${index}`.
       *
       * A positional id is only addressable from inside this component. Anything
       * outside it — the `focusScanId` deep-link below, and anything that later
       * wants to link to a scan — knows a scan by its real id and has no way to
       * learn which array slot it landed in, least of all after `filterScans` and
       * the coordinate filter have both removed rows.
       *
       * Positional ids were also fragile in place: the index is taken BEFORE the
       * no-coordinates filter, so `scan-3` meant different things depending on how
       * many earlier rows lacked a location. Nothing depended on that, because
       * nothing outside could.
       *
       * The fallback keeps the old shape for a row with no id at all (none observed
       * — `ScanLog.id` is non-optional — but `Map` keys its markers on this, and a
       * duplicate `undefined` across several rows would collapse them into one).
       */
      id: item.id ?? `scan-${index}`,
      // Explicit fallback: getNameOrEmailSlug returns null for an unidentifiable user
      // (VERA-476 hardening), and without this the card rendered a blank first line.
      owner: getNameOrEmailSlug(item.user ?? undefined) || 'Anonymous',
      image: item.user?.avatar ?? '',
      lat: item.metadata?.latitude ?? item.metadata?.location?.latitude ?? null,
      lng: item.metadata?.longitude ?? item.metadata?.location?.longitude ?? null,
      // Same relative-then-absolute formatter the list rows use. The card previously
      // showed a raw 'Pp' timestamp, so the identical scan read "6 minutes ago" in the
      // list and "08/02/2026, 2:44 AM" on the map.
      date: formatActivityTime(item.created_at),
      // Human-readable place instead of raw coordinates (VERA-476). The reverse-
      // geocoded `formatted_address` is already on this payload — no extra fetch —
      // it just wasn't declared on the ScanLog type, so nothing consumed it.
      locationLabel: getScanLocationLabel(item.metadata),
      // Coloured by the SCAN's outcome, not the certificate's display status. It used to
      // read `certificate_display_status`, which is a property of the certificate rather
      // than of any one scan — so every pin on a given certificate came out the same
      // colour and a tampered or invalid scan was indistinguishable from a clean one on
      // the map, which is the one place colour is the only signal there is.
      //
      // Default is brand purple (the verified case); it moves off purple only when the
      // scan itself says something is wrong. Same resolver as the chip, so pin and chip
      // can't disagree.
      fillColor: getScanStatus(item as any, { isDark: !isLightTheme }).color,
      // Scan outcome, so the card can show the same chip the list row does.
      status: getScanStatus(item as any),
      // Severity + colour travel with the item so the shared Map can decide which member
      // of a STACK speaks for the pin. Without these the "most alerting member" pick was
      // inert (every priority defaulted to 0), so a stack showed whichever scan happened
      // to be FIRST — a clean scan could sit on top of an invalid one at the same spot
      // and the pin would look fine.
      clusterPriority: getScanStatus(item as any).severity,
      clusterColor: getScanStatus(item as any, { isDark: !isLightTheme }).color,
    }));

    // remove items without location
    const filtered = list?.filter(item => item.lat !== null && item.lng !== null) ?? [];

    return filtered;
  };

  const scanList = useMemo(() => getScanList(), [history?.data, filters]);

  // Convert to MapItem format
  const mapItems: MapItem[] = useMemo(() => {
    return scanList.map(scan => ({
      id: scan.id,
      lat: scan.lat,
      lng: scan.lng,
      owner: scan.owner,
      image: scan.image,
      date: scan.date,
      fillColor: scan.fillColor,
      locationLabel: scan.locationLabel,
      status: scan.status,
      clusterPriority: scan.clusterPriority,
      clusterColor: scan.clusterColor,
    }));
  }, [scanList]);

  // One scan's row. Shared by the single-pin card and every row of a stack, so a scan
  // reads identically however you arrived at it.
  const scanRow = (item: MapItem, opts?: { hideLocation?: boolean }) => {
    const status = (item as MapItem & { status?: any }).status;
    return (
      <Stack direction='row' alignItems='center' justifyContent='space-between' spacing={2}>
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <OwnerBlock
            owner={item.owner}
            subTitle={item.date}
            // Address (or City, State, Country) rather than raw coordinates. Falls back
            // to coordinates inside getScanLocationLabel when nothing else is known —
            // a pin always has them, so the line is never empty.
            //
            // Omitted inside a stack: a stack is BY DEFINITION scans at the same
            // coordinates (6dp ≈ 11cm), so the place is identical on every row and is
            // already the list's subheading. Repeating it per row is pure noise, and in a
            // scroll area it costs a third of the height of every row to say nothing.
            title={
              opts?.hideLocation
                ? undefined
                : ((item as MapItem & { locationLabel?: string | null }).locationLabel ??
                  `${item.lat?.toFixed(4)}, ${item.lng?.toFixed(4)}`)
            }
            imageUrl={item.image}
          />
        </Box>
        {status?.showChip && (
          <Box sx={{ flexShrink: 0 }}>
            {/* Only "Opened" is tappable: it is the one state with an explainer sheet
                (VERA-628). The others carry a `description` on the resolver but no sheet
                to show it, and a chip that looks tappable and does nothing is worse than
                one that plainly isn't. */}
            <ScanStatusChip
              status={status}
              onClick={status.key === 'tampered' ? () => setSealSheetOpen(true) : undefined}
            />
          </Box>
        )}
      </Stack>
    );
  };

  /**
   * The docked card under the map. Shows the stack when one is open, otherwise the single
   * selected scan.
   *
   * Clicking a stack used to open an InfoWindow tooltip ON the map while this card kept
   * showing the PREVIOUS single scan — two panels describing two different places at once,
   * with the stale one directly under the fresh one. Routing the stack here means one
   * surface owns scan detail. It also gets the rows out of an HTML string and back into
   * React: real `ScanStatusChip`, real avatars, the app's font, and no width ceiling from
   * the InfoWindow (which is bounded by the map card, and truncated timestamps at 375px).
   */
  const renderOverlay = (activeItem: MapItem | null) => {
    if (stackItems?.length) {
      const place = (stackItems[0] as MapItem & { locationLabel?: string | null })?.locationLabel;
      return (
        <Box
          // Keyed by WHICH stack is open so React remounts the node when you tap a
          // different pin. Without it React reuses the same element, the CSS animation
          // never restarts, and only the first stack you ever open animates.
          key={`stack-${stackItems[0]?.lat},${stackItems[0]?.lng}-${stackItems.length}`}
          sx={theme => ({
            // NO bottom padding: the scroll area has to run all the way to the card's
            // bottom border. With padding there, the half-cut row floated above a band of
            // white, which reads as the list having ended rather than continuing.
            padding: '12px 16px 0',
            backgroundColor: theme.palette.background.paper,
            color: theme.palette.text.primary,
            // The card GROWS when a stack opens (one row becomes a header plus 2.5), and
            // the first version animated only opacity + a 10px lift — so the content
            // faded in while the panel's height still jumped in a single frame, shoving
            // the map up. Animating `max-height` is what makes it actually slide open;
            // the lift rides along so the rows arrive from below rather than materialising.
            //
            // `overflow: hidden` is what makes the max-height animation read as a reveal
            // instead of a squash. The end value only has to EXCEED the tallest card
            // (header + 2.5 rows ~ 190px) — max-height cannot animate to `auto`, and
            // overshooting only costs a slightly faster final few pixels.
            overflow: 'hidden',
            '@keyframes veraStackRise': {
              from: { opacity: 0, transform: 'translateY(14px)', maxHeight: 0 },
              to: { opacity: 1, transform: 'none', maxHeight: '320px' },
            },
            animation: 'veraStackRise 260ms cubic-bezier(0.22, 1, 0.36, 1)',
            '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
          })}
        >
          {/* Place LEFT, count RIGHT, on one line. They were stacked, which spent two
              lines of a short card on a heading. */}
          <Stack
            direction='row'
            justifyContent='space-between'
            alignItems='baseline'
            spacing={2}
            sx={{ mb: 1 }}
          >
            {/* 12px = `body2`, the SAME size OwnerBlock renders an address at (its
                `title` line). This header holds the identical field — `locationLabel` —
                that a single-scan row shows, and it is often a full street address rather
                than "Berlin, DE", so it has to be sized like an address or the two
                surfaces disagree about the same data and long values stop fitting.
                Weight and colour carry the hierarchy instead of size. */}
            <Box
              sx={{
                fontSize: `${font.label}px`,
                lineHeight: 1.35,
                fontWeight: 600,
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {place}
            </Box>
            <Box
              // Same 12px as the place. Leaving it at 14 made the secondary count LARGER
              // than the thing it qualifies, inverting the hierarchy; weight and colour
              // separate them instead.
              sx={{ fontSize: `${font.label}px`, lineHeight: 1.35, color: 'text.secondary', flexShrink: 0 }}
            >
              {stackItems.length} scans here
            </Box>
          </Stack>
          {/* Capped at 2.5 rows. The .5 is the load-bearing part: a half-cut row is what
              tells the reader the list scrolls. A WHOLE number of rows looks like the whole
              list, so a stack of 6 would silently read as a stack of 2. Was 1.5, which was
              honest but cramped — two full rows give enough of the list to judge it
              without the card eating the map. */}
          <Box
            sx={{
              maxHeight: STACK_ROW_HEIGHT * 2.5,
              overflowY: 'auto',
              overscrollBehavior: 'contain',
              // Scrollbar hidden: the half-cut row is already the "there is more" signal,
              // so the bar is a second, uglier copy of it — and on a 375px card it eats
              // width from the status chips. Scrolling still works by touch and wheel.
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              '&::-webkit-scrollbar': { display: 'none' },
              // The map sits directly above; without this a flick that overshoots the end
              // of the list scrolls the page and drags the map out from under the finger.
              WebkitOverflowScrolling: 'touch',
            }}
          >
            <Stack divider={<Box sx={{ height: '1px', bgcolor: 'divider' }} />}>
              {stackItems.map((item, i) => (
                <Box key={`${item.lat},${item.lng},${i}`} sx={{ py: 1 }}>
                  {scanRow(item, { hideLocation: true })}
                </Box>
              ))}
            </Stack>
          </Box>
        </Box>
      );
    }

    if (!activeItem) return null;
    return (
      <Box
        sx={theme => ({
          padding: '12px 16px',
          backgroundColor: theme.palette.background.paper,
          color: theme.palette.text.primary,
        })}
      >
        {/* Identity left, status RIGHT — the same split the list rows use, so a scan
            reads identically in either view (VERA-476). The chip sat above the block,
            left-aligned, which broke that parity. Verified scans stay unbadged here too,
            so the chip only appears when something is wrong. */}
        {scanRow(activeItem)}
      </Box>
    );
  };

  return (
    <>
    <Map
      items={mapItems}
      apiKey={GOOGLE_MAPS_API_KEY || ''}
      height={mapHeight}
      defaultZoom={defaultZoom}
      mapStyle={isLightTheme ? 'light' : 'dark'}
      enableClustering={true}
      isLoading={isLoadingScans}
      showLoadingState={true}
      showErrorState={true}
      emptyMessage='No scan locations available yet'
      errorMessage='Google Maps API key not configured'
      renderOverlay={renderOverlay}
      // VERA-315: the deep-linked scan, when there is one. `undefined` (not null) —
      // `Map` treats any falsy value as "no selection", but the prop is typed
      // `string | undefined` and passing null would be a type error rather than a
      // behaviour change.
      activeItemId={focusScanId ?? undefined}
      // Stack members come HERE rather than into a map tooltip — see `renderOverlay`.
      onStackClick={setStackItems}
      onMarkerClick={handleMarkerClick}
      // Docked under the map rather than floating over it (VERA-476): on a 375px screen
      // a floating card covered a meaningful slice of the map — including the pin it was
      // describing — to show three lines of text. Below the map it gets the full width
      // and costs the map nothing.
      overlayPosition='below'
      autoZoomToLast={true}
    />
    {/* This surface owns its own instance rather than lifting a callback up through
        ScansTabContent to CertificatePage. Both are controlled and only one is ever
        open, so the cost is a mount, not a behaviour. */}
    <SealOpenedDrawer
      open={sealSheetOpen}
      onClose={() => setSealSheetOpen(false)}
      tagOpenedAt={certificate?.tag_opened_at}
      supportEmail={certificate?.company?.support_email}
      supportPhone={certificate?.company?.support_phone}
    />
    </>
  );
};
